import { useAuth0 } from '@auth0/auth0-react';
import type { AutocompleteOption } from '@talentmesh/ui/components/autocomplete-chips';
import useSWR from 'swr';
import { GeocodeResponse } from './DTO/GeocodeResponse';
import useAISearchProgress from './useAISearchProgress';
import ResourceNotFoundError from '@/Utils/HTTP/Errors/ResourceNotFoundError';
import http from '@/Utils/HTTP/NewHttpUtils';

interface YearsOfExperienceRange {
    min: number;
    max: number;
}

export type SearchPeriod = 'Current' | 'Last3Years' | 'Last5Years' | 'AllTime';

export const SearchPeriodList: Array<SearchPeriod> = ['Current', 'Last3Years', 'Last5Years', 'AllTime'];

export type LanguageProficiency =
    | 'AnyProficiency'
    | 'ElementaryProficiency'
    | 'LimitedWorkingProficiency'
    | 'ProfessionalWorkingProficiency'
    | 'FullProfessionalProficiency'
    | 'NativeOrBilingualProficiency';

export const LanguageProficiencyLevelList: Array<LanguageProficiency> = [
    'AnyProficiency',
    'ElementaryProficiency',
    'LimitedWorkingProficiency',
    'ProfessionalWorkingProficiency',
    'FullProfessionalProficiency',
    'NativeOrBilingualProficiency',
];

export interface SearchFilterResponse {
    id: string;
    jobTitleSearchPeriod: SearchPeriod;
    jobTitles: string[];
    locationRadius: number;
    locations: GeocodeResponse[];
    companiesSearchPeriod: SearchPeriod;
    companies: string[];
    keywords: string[];
    yearsOfExperience: YearsOfExperienceRange;
    includeEmptyExperience: boolean;
    languages: string[];
    languageProficiency: LanguageProficiency;
}

export const DefaultSearchFilterResponse: SearchFilterResponse = {
    id: '',
    jobTitleSearchPeriod: 'Current',
    jobTitles: [],
    locationRadius: 1,
    locations: [],
    companiesSearchPeriod: 'Current',
    companies: [],
    keywords: [],
    yearsOfExperience: {
        min: 0,
        max: 30,
    },
    includeEmptyExperience: false,
    languages: [],
    languageProficiency: 'AnyProficiency',
};

export interface RefineSearchFilterFormValues {
    jobTitles: AutocompleteOption[];
    jobTitleSearchPeriod: SearchPeriod;
    companies: AutocompleteOption[];
    companiesSearchPeriod: SearchPeriod;
    locations: GeocodeResponse[];
    locationRadius: number;
    keywords: AutocompleteOption[];
    yearsOfExperience: [number, number];
    includeEmptyExperience: boolean;
    languages: AutocompleteOption[];
    languageProficiency: LanguageProficiency;
}

export const DefaultRefineSearchFilters: RefineSearchFilterFormValues = {
    jobTitleSearchPeriod: 'Current',
    jobTitles: [],
    // minimum is 1
    locationRadius: 1,
    locations: [],
    companiesSearchPeriod: 'Current',
    companies: [],
    keywords: [],
    yearsOfExperience: [0, 30],
    includeEmptyExperience: false,
    languages: [],
    languageProficiency: 'AnyProficiency',
};

/**
 * Converts SearchFilterResponse from API to IdentifiedFilters format
 * @param searchFiltersData - The response from useSearchFilters hook
 * @returns Converted IdentifiedFilters object
 */
export const convertSearchFiltersToIdentifiedFilters = (
    searchFiltersData?: SearchFilterResponse,
): RefineSearchFilterFormValues => {
    return {
        jobTitles:
            searchFiltersData?.jobTitles?.map((title) => ({ label: title })) || DefaultRefineSearchFilters.jobTitles,
        jobTitleSearchPeriod:
            searchFiltersData?.jobTitleSearchPeriod ?? DefaultRefineSearchFilters.jobTitleSearchPeriod,
        companies:
            searchFiltersData?.companies?.map((company) => ({ label: company })) ||
            DefaultRefineSearchFilters.companies,
        companiesSearchPeriod:
            searchFiltersData?.companiesSearchPeriod ?? DefaultRefineSearchFilters.companiesSearchPeriod,
        locationRadius: searchFiltersData?.locationRadius || DefaultRefineSearchFilters.locationRadius,
        locations: searchFiltersData?.locations || DefaultRefineSearchFilters.locations,
        keywords:
            searchFiltersData?.keywords?.map((keyword) => ({ label: keyword })) || DefaultRefineSearchFilters.keywords,
        yearsOfExperience: searchFiltersData?.yearsOfExperience
            ? [searchFiltersData?.yearsOfExperience.min, searchFiltersData?.yearsOfExperience.max]
            : DefaultRefineSearchFilters.yearsOfExperience,
        includeEmptyExperience:
            searchFiltersData?.includeEmptyExperience ?? DefaultRefineSearchFilters.includeEmptyExperience,
        languages:
            searchFiltersData?.languages?.map((language) => ({ label: language })) ||
            DefaultRefineSearchFilters.languages,
        languageProficiency: searchFiltersData?.languageProficiency ?? DefaultRefineSearchFilters.languageProficiency,
    };
};

export const useSearchFilters = (recruitmentId: string) => {
    const { getAccessTokenSilently } = useAuth0();
    const { data } = useAISearchProgress();

    return useSWR(
        data?.isInProgress !== false ? null : `recruitments/${recruitmentId}/ai-search/search-filters`,
        async (url) => {
            try {
                const response = await http.get<SearchFilterResponse>({
                    path: url,
                    accessToken: await getAccessTokenSilently(),
                });

                return response || DefaultSearchFilterResponse;
            } catch (error: any) {
                if (error instanceof ResourceNotFoundError) {
                    return DefaultSearchFilterResponse;
                }

                throw error;
            }
        },
    );
};
